import { ShiftsLocationFragment } from 'generated/resources/graphql'
import uniq from 'lodash/uniq'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'

import Holiday, {
  Props as HolidayAttributes,
} from 'modules/shift_management/store/internal/models/Holiday'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

export interface HolidayInfo {
  locationId: number
  summary: string
}

class Holidays {
  holidays: Holiday[]

  constructor(holidays: Holiday[]) {
    this.holidays = holidays

    makeObservable(this, {
      holidaysLength: computed,
      holidaysInformation: computed,
    })
  }

  /**
   * Transforms an array of objects from a request into an array of Holiday
   * objects
   */

  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromProps(holidays: Array<CompanyHoliday | HolidayAttributes>): Holidays {
    const holidayObjects = holidays.map((holiday) => {
      if ('attributes' in holiday) {
        return new Holiday({ ...holiday.attributes } as HolidayAttributes)
      }

      return new Holiday(holiday)
    })

    return new Holidays(holidayObjects)
  }

  static buildFromGraphQL(location: ShiftsLocationFragment | undefined | null): Holidays {
    const holidays = location?.companyHolidays || []
    const holidayObjects = holidays.map((holiday) => Holiday.buildFromGraphQL(holiday, location))

    return new Holidays(holidayObjects)
  }

  get holidaysLength(): number {
    return this.holidays.length
  }

  get holidaysInformation(): (string | undefined)[] {
    return uniq(
      this.holidays.map((holiday) => {
        return holiday.location
          ? `${holiday.summary} - ${holiday.location.name}`
          : `${holiday.summary}`
      })
    )
  }

  filterByDate(date: moment.Moment): Holiday[] {
    return this.holidays.filter((holiday) => date.isSame(holiday.date, 'day'))
  }

  filterByDateAndLocation(date: moment.Moment, id: number): Holiday[] {
    return this.holidays.filter((holiday) => holiday.isDate(date) && holiday.location?.id === id)
  }
}

export default Holidays
